import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiSave, FiGlobe, FiMail, FiPackage } from 'react-icons/fi';
import superadminService from '../../../services/superadminService';
import { TenantCreate, DomainConfig, PlanType } from '../../../types/tenant';
import { useNotification } from '../../../contexts/NotificationContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  h2 {
    font-size: 2rem;
    color: #333;
    margin: 0;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
  
  svg {
    font-size: 1.25rem;
    color: #666;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  color: #333;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #ff6b6b;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 1rem;
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;
  
  input {
    cursor: pointer;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  color: #666;
  border: 1px solid #ddd;
  
  &:hover:not(:disabled) {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
`;

const SaveButton = styled(Button)`
  background-color: #ff6b6b;
  color: white;
  border: none;
  
  &:hover:not(:disabled) {
    background-color: #ff5252;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const plans: { value: PlanType; label: string; description: string }[] = [
  { value: 'trial', label: 'Trial', description: '14 dias grátis para testar' },
  { value: 'basic', label: 'Básico', description: 'Ideal para pequenas imobiliárias' },
  { value: 'professional', label: 'Profissional', description: 'Recursos avançados para crescer' },
  { value: 'enterprise', label: 'Enterprise', description: 'Solução completa personalizada' },
];

const NewTenant: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState<TenantCreate>({
    name: '',
    slug: '',
    plan_type: 'trial',
    billing_email: '',
  });
  
  const [domainConfig, setDomainConfig] = useState<DomainConfig>({
    domain: '',
    include_www: true,
    include_api: true,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slug from name
    if (name === 'name' && !formData.slug) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setDomainConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.name || !formData.slug) {
      setError('Nome e slug são obrigatórios');
      return;
    }
    
    if (!domainConfig.domain) {
      setError('Domínio é obrigatório');
      return;
    }
    
    if (!formData.billing_email) {
      setError('Email de cobrança é obrigatório');
      return;
    }
    
    try {
      setLoading(true);
      const response = await superadminService.createTenant(formData, domainConfig);
      showNotification('Tenant criado com sucesso!', 'success');
      navigate(`/superadmin/tenants/${response.id}`);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Erro ao criar tenant');
      showNotification('Erro ao criar tenant', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/superadmin/tenants')}>
          <FiArrowLeft />
        </BackButton>
        <h2>Novo Tenant</h2>
      </Header>

      <form onSubmit={handleSubmit}>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Card>
          <CardTitle>
            <FiPackage />
            Informações Básicas
          </CardTitle>
          
          <FormGroup>
            <Label>Nome da Empresa *</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Imobiliária Silva"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Slug (identificador único) *</Label>
            <Input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              placeholder="Ex: imobiliaria-silva"
              pattern="^[a-z0-9-]+$"
              required
            />
            <HelpText>
              Apenas letras minúsculas, números e hífen. Será usado em URLs.
            </HelpText>
          </FormGroup>
          
          <FormGroup>
            <Label>Plano *</Label>
            <Select
              name="plan_type"
              value={formData.plan_type}
              onChange={handleChange}
              required
            >
              {plans.map(plan => (
                <option key={plan.value} value={plan.value}>
                  {plan.label} - {plan.description}
                </option>
              ))}
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Email de Cobrança *</Label>
            <Input
              type="email"
              name="billing_email"
              value={formData.billing_email}
              onChange={handleChange}
              placeholder="financeiro@empresa.com.br"
              required
            />
            <HelpText>
              Email para envio de faturas e comunicações sobre o plano.
            </HelpText>
          </FormGroup>
        </Card>
        
        <Card>
          <CardTitle>
            <FiGlobe />
            Configuração de Domínio
          </CardTitle>
          
          <FormGroup>
            <Label>Domínio Principal *</Label>
            <Input
              type="text"
              name="domain"
              value={domainConfig.domain}
              onChange={handleDomainChange}
              placeholder="Ex: imobiliariasilva.com.br"
              required
            />
            <HelpText>
              Domínio principal do tenant (sem http:// ou https://)
            </HelpText>
          </FormGroup>
          
          <CheckboxGroup>
            <Checkbox>
              <input
                type="checkbox"
                name="include_www"
                checked={domainConfig.include_www}
                onChange={handleDomainChange}
              />
              Incluir subdomínio www
            </Checkbox>
            
            <Checkbox>
              <input
                type="checkbox"
                name="include_api"
                checked={domainConfig.include_api}
                onChange={handleDomainChange}
              />
              Incluir subdomínio api
            </Checkbox>
          </CheckboxGroup>
        </Card>
        
        <Actions>
          <CancelButton
            type="button"
            onClick={() => navigate('/superadmin/tenants')}
            disabled={loading}
          >
            Cancelar
          </CancelButton>
          
          <SaveButton type="submit" disabled={loading}>
            <FiSave />
            {loading ? 'Criando...' : 'Criar Tenant'}
          </SaveButton>
        </Actions>
      </form>
    </Container>
  );
};

export default NewTenant;